<template>
  <div class="out_of_stock">
    
<FormKitLazyProvider>
<div
      v-if="!submittedData"
      class="out_of_stock__form-wrapper"
    >
      <div class="out_of_stock__label">
        <span>{{ $t('Email me when my friend is available again.') }}</span>
      </div>
      <FormKit
        id="outOfStockForm"
        type="form"
        :incomplete-message="$t('Sorry, not all fields are filled out correctly.')"
        form-class="out_of_stock__form"
        :actions="false"
        @submit="handleSubmit"
      >
        <FormKit
          id="stock_email"
          v-model="email"
          type="email"
          name="email"
          :placeholder="$t('Email address')"
          validation="required|email"
          validation-visibility="blur"
          :validation-messages="{
            required: $t('This is a required field.'),
            email: $t('Please enter a valid email address.'),
          }"
        />
        <div
          v-if="hasError"
          ref="error"
          class="message message--error required"
        >
          <IconInfo class="message__icon icon icon--info" />
          <span>{{ $t(errorLabel) }}</span>
        </div>
        <button
          type="submit"
          class="out_of_stock__button button button--cta"
          :disabled="loading"
        >
          <span>{{ $t('Notify me') }}</span>
        </button>
      </FormKit>
    </div>

    <div
      v-if="submittedData"
      class="out_of_stock__submitted"
    >
      <div class="out_of_stock__success">
        <span
          v-html="$t('We\'ll send an email to {email} when size {size} of this item is back in stock.', { email: submittedData.email, size: submittedData.size })"
        />
      </div>

      <button
        type="button"
        class="out_of_stock__continue button button--cta"
        @click="closeOosDialog"
      >
        <span>{{ $t('Continue Shopping') }}</span>
      </button>
    </div>
</FormKitLazyProvider>

  </div>
</template>

<script lang="ts" setup>
import { FormKitLazyProvider } from '@formkit/vue'
import { useI18n } from 'vue-i18n'
import { reset } from '@formkit/core'
import type { MagentoSimpleProduct, MagentoConfigurableProduct } from '~/types/magento'

export interface Props {
  products: MagentoSimpleProduct[]
  parentProduct?: MagentoConfigurableProduct
  itemSize?: string
}
const props = defineProps<Props>()

const { t } = useI18n()
const { email, loading, submit, resetForm } = useStockNotification()

const { close: closeOosDialog, settings: oosDialogSettings } = useUiDialog('product-oos-dialog')

const hasError = ref(false)
const errorLabel = ref('')
const error = ref<HTMLElement | null>(null)
const submittedData = ref<{ email: string, size?: string } | null>(null)

onClickOutside(error, () => {
  hasError.value = false
})

watch(
  () => oosDialogSettings.value.isOpen,
  (isOpen, wasOpen) => {
    if (wasOpen && !isOpen) {
      submittedData.value = null
      hasError.value = false
    }
  },
)

const handleSubmit = async () => {
  try {
    for (const product of props.products) {
      submittedData.value = { email: email.value, size: props.itemSize }

      const success = await submit(
        product.id,
        props.parentProduct?.id,
        props.parentProduct,
        props.itemSize,
        product.sku,
      )
      if (!success) {
        hasError.value = true
        errorLabel.value = t('Failed to subscribe')
        submittedData.value = null
        return
      }
    }

    reset('stock_email')
    resetForm()
    hasError.value = false
  }
  catch (e) {
    hasError.value = true
    errorLabel.value = t('Failed to subscribe')
    submittedData.value = null
    console.error(e)
  }
}
</script>

<style src="~layers/app/components/Product/OutOfStockNotification.css" />
